// Simple request deduplication and caching utility
class RequestCache {
  constructor() {
    this.cache = new Map();
    this.pendingRequests = new Map();
  }

  // Generate cache key from request details
  generateKey(url, params = {}) {
    const sortedParams = Object.keys(params)
      .sort()
      .reduce((result, key) => {
        result[key] = params[key];
        return result;
      }, {});

    return `${url}?${JSON.stringify(sortedParams)}`;
  }

  // Get cached data if available and not stale
  get(key, maxAge = 5 * 60 * 1000) { // 5 minutes default
    const entry = this.cache.get(key);
    if (!entry) {
      console.log(`🔍 Cache MISS for: ${key}`);
      return null;
    }

    if (Date.now() - entry.timestamp > maxAge) {
      console.log(`⏰ Cache EXPIRED for: ${key}`);
      this.cache.delete(key);
      return null;
    }

    console.log(`✅ Cache HIT for: ${key} (age: ${Math.round((Date.now() - entry.timestamp) / 1000)}s)`);
    return entry.data;
  }

  // Set cache data
  set(key, data) {
    console.log(`💾 Cache SET for: ${key}`);
    this.cache.set(key, {
      data,
      timestamp: Date.now()
    });
  }

  // Get pending request promise to avoid duplicate requests
  getPendingRequest(key) {
    return this.pendingRequests.get(key);
  }

  // Set pending request promise
  setPendingRequest(key, promise) {
    this.pendingRequests.set(key, promise);

    // Clean up when promise resolves/rejects
    promise.finally(() => {
      this.pendingRequests.delete(key);
    });

    return promise;
  }

  // Clear all cache
  clear() {
    this.cache.clear();
    this.pendingRequests.clear();
  }

  // Clear cache for specific pattern
  clearPattern(pattern) {
    for (const key of this.cache.keys()) {
      if (key.includes(pattern)) {
        this.cache.delete(key);
      }
    }
  }
}

// Export singleton instance
export const requestCache = new RequestCache();

// Cached request wrapper
export const cachedRequest = async (requestFn, cacheKey, maxAge) => {
  // Check cache first
  const cached = requestCache.get(cacheKey, maxAge);
  if (cached) {
    return cached;
  }

  // Check if request is already pending
  const pending = requestCache.getPendingRequest(cacheKey);
  if (pending) {
    return pending;
  }

  // Make new request
  const promise = requestFn();
  requestCache.setPendingRequest(cacheKey, promise);

  try {
    const result = await promise;
    requestCache.set(cacheKey, result);
    return result;
  } catch (error) {
    // Don't cache errors
    throw error;
  }
};
